import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { ImageService } from '../services/image.service';
import { ProductReference } from "../models/layout/product-reference";
import { ProductOrientation } from "../models/layout/product-orientation";
import { OverlayService, OverlayType } from '../services/overlay.service';
import { LayoutService } from '../services/layout.service';
import { VisualAnalysisService } from '../services/visualanalysis.service';
import { NgStyle, AsyncPipe } from '@angular/common';

const defaultImage =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
const loadingGif = '/images/product-loading.gif';

@Component({
    selector: 'app-image',
    templateUrl: './image.component.html',
    styleUrl: './image.component.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [NgStyle, AsyncPipe],
})
export class ImageComponent implements OnInit, OnDestroy, OnChanges {
  @Input() productId: number | undefined;
  @Input() thumbnail: boolean | undefined;
  @Input() orientation: ProductOrientation | undefined;
  @Input() squeeze: number | undefined;
  @Input() fallbackBarcode: string | undefined;
  // Per-placement tray/case variant: 1=tray, 2=case. Selects the barcode+'T'/'C' image.
  @Input() caseConfig?: number | null;

  containerStyle$ = new BehaviorSubject<{ [key: string]: string }>({
    display: 'none',
  });
  isLoading$ = new BehaviorSubject<boolean>(false);

  private productRef: ProductReference | null = null;
  private overlayStyle: { [key: string]: string } = {};
  private currentImageUrl: string | null = null;
  private loadImageSubscription?: Subscription;
  private overlaySubscription?: Subscription;
  private imageUpdateSubscription?: Subscription;

  constructor(
    private imageService: ImageService,
    private layoutService: LayoutService,
    private overlayService: OverlayService,
    private visualAnaylsisService: VisualAnalysisService,
    private cdr: ChangeDetectorRef
  ) {}

  // The barcode actually requested: tray/case placements reuse the unit barcode with a
  // 'T'/'C' suffix; units use the barcode as-is.
  private get effectiveBarcode(): string {
    const barcode = this.productRef?.barcode ?? '';
    if (this.caseConfig === 1) return barcode + 'T';
    if (this.caseConfig === 2) return barcode + 'C';
    return barcode;
  }

  // A tray/case image that was never uploaded falls back to the unit image.
  private get effectiveFallbackBarcode(): string | undefined {
    if (this.caseConfig === 1 || this.caseConfig === 2) {
      return this.fallbackBarcode ?? this.productRef?.barcode;
    }
    return this.fallbackBarcode;
  }

  ngOnInit() {
    if (this.productId != null && this.thumbnail != null) {
      this.productRef =
        this.layoutService.planogramItems.get(this.productId) ?? null;

      if (this.productRef != null && this.orientation != null) {
        if (
          !this.imageService.isImageCached(
            this.effectiveBarcode,
            this.orientation!
          )
        ) {
          // Show loading GIF immediately
          this.updateContainerStyle();
          this.cdr.detectChanges();
        }

        this.loadImageSubscription = this.imageService
          .getImage(this.effectiveBarcode, this.orientation!, this.effectiveFallbackBarcode)
          .subscribe({
            next: (imageSrc) => {
              this.updateContainerStyle(imageSrc);
            },
            error: (error) => {
              console.error(
                `[ImageComponent] Error loading image for product ${this.productId}:`,
                error
              );
              this.updateContainerStyle(defaultImage);
            },
          });

        // Subscribe to image updates
        this.imageUpdateSubscription =
          this.imageService.imageUpdates$.subscribe((update) => {
            if (
              this.productRef &&
              this.effectiveBarcode === update.barcode &&
              this.orientation === update.orientation
            ) {
              // Reload the image
              this.loadImage();
            }
          });
      }
    }

    // Handle overlay changes
    this.overlaySubscription = combineLatest([
        this.overlayService.overlayType$,
        this.visualAnaylsisService.customColorMap$,
    ]).subscribe(([overlayType, customColorMap]) => {
        this.updateOverlayStyle(overlayType, customColorMap);
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['productId'] ||
      changes['thumbnail'] ||
      changes['orientation'] ||
      changes['squeeze'] ||
      changes['fallbackBarcode'] ||
      changes['caseConfig']
    ) {
      if (this.productId == null) return;

      // Cleanup existing image subscription
      if (this.loadImageSubscription) {
        this.loadImageSubscription.unsubscribe();
      }

      // Refresh product data
      this.productRef =
        this.layoutService.planogramItems.get(this.productId) ?? null;

      if (this.productRef != null && this.orientation != null) {
        if (
          !this.imageService.isImageCached(
            this.effectiveBarcode,
            this.orientation
          )
        ) {
          // Show loading state immediately
          this.updateContainerStyle();
          this.cdr.detectChanges();
        }

        // Load new image for current orientation
        this.loadImageSubscription = this.imageService
          .getImage(this.effectiveBarcode, this.orientation, this.effectiveFallbackBarcode)
          .subscribe({
            next: (imageSrc) => {
              this.updateContainerStyle(imageSrc);
            },
            error: (error) => {
              console.error(
                `[ImageComponent] Error loading image for product ${this.productId}:`,
                error
              );
              this.updateContainerStyle(defaultImage);
            },
          });
      } else {
        this.updateContainerStyle();
      }
    }
  }

  ngOnDestroy() {
    // Cleanup subscriptions
    if (this.loadImageSubscription) {
      this.loadImageSubscription.unsubscribe();
    }

    if (this.overlaySubscription) {
      this.overlaySubscription.unsubscribe();
    }
    
    if (this.imageUpdateSubscription) {
      this.imageUpdateSubscription.unsubscribe();
    }

    // Cleanup BehaviorSubject
    if (this.containerStyle$) {
      this.containerStyle$.complete();
    }

    // Clear references
    this.productRef = null;
    this.currentImageUrl = null;
    this.overlayStyle = {};
  }

  // ... existing code ...
  private updateOverlayStyle(overlayType: OverlayType, customColorMap: Map<number, string>) {
    if (!this.productId) return;

    if (overlayType === OverlayType.NONE) {
      this.overlayStyle = {};
    } else if (overlayType === OverlayType.VISUAL_ANALYSIS) {
      // VA filters out groups via toggleGroupVisibility (clears their custom
      // color); those products should stay fully opaque so the un-categorised
      // image is visible. Only tint when a custom color is set.
      const color = customColorMap.get(this.productId);
      this.overlayStyle = color ? { opacity: '0.5' } : {};
    } else {
      // Non-VA overlays paint every product via productLegendMap on the outer
      // .product div's background-color. The image must be translucent for
      // that color to show through.
      this.overlayStyle = { opacity: '0.5' };
    }

    if (this.currentImageUrl) {
      this.updateContainerStyle(this.currentImageUrl);
    }
  }

  // Add helper method to load image
  private loadImage() {
    if (this.productRef && this.orientation != null) {
      if (!this.imageService.isImageCached(this.effectiveBarcode, this.orientation)) {
        this.updateContainerStyle();
        this.cdr.detectChanges();
      }

      if (this.loadImageSubscription) {
        this.loadImageSubscription.unsubscribe();
      }

      this.loadImageSubscription = this.imageService
        .getImage(this.effectiveBarcode, this.orientation, this.effectiveFallbackBarcode).subscribe({
          next: (imageSrc) => {
            this.updateContainerStyle(imageSrc);
          },
          error: (error) => {
            console.error(`[ImageComponent] Error loading image for product ${this.productId}:`, error);
            this.updateContainerStyle(defaultImage);
          }
        });
    }
  }

  private updateContainerStyle(imageUrl?: string | SafeUrl) {
    if (this.productRef == null) {
      this.containerStyle$.next({ display: 'none' });
      this.isLoading$.next(false);
      return;
    }

    let backgroundImage: string;
    if (!imageUrl) {
      backgroundImage = `url(${defaultImage})`; // Show transparent background while loading
      this.isLoading$.next(true); // Show loading spinner
    } else if (typeof imageUrl === 'string') {
      backgroundImage = `url(${imageUrl})`;
      this.currentImageUrl = imageUrl;
      this.isLoading$.next(false); // Hide loading spinner
    } else {
      // Handle SafeUrl
      const urlString = (imageUrl as any).changingThisBreaksApplicationSecurity;
      if (urlString) {
        backgroundImage = `url(${urlString})`;
        this.currentImageUrl = urlString;
        this.isLoading$.next(false); // Hide loading spinner
      } else {
        backgroundImage = `url(${defaultImage})`;
        this.isLoading$.next(false); // Hide loading spinner
      }
    }

    const newStyle = {
      'background-image': backgroundImage,
      display: 'block',
      ...this.overlayStyle,
    };

    this.containerStyle$.next(newStyle);
  }
}
